import { afterNextRender, Component, computed, effect, ElementRef, inject, OnDestroy, PLATFORM_ID, signal, viewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { KeenSliderInstance } from 'keen-slider';
import KeenSlider from 'keen-slider';
import { SignalsStoreService } from '../signals-store.service';
import { RouterModule } from '@angular/router';
import { GlobalMessageService } from './global-message.service';
import { map, tap } from 'rxjs';

@Component({
    selector: 'app-global-message',
    imports: [
        RouterModule
    ],
    templateUrl: './global-message.component.html',
    styleUrl: './global-message.component.scss'
})
export class GlobalMessageComponent implements OnDestroy {

  //#region Services

  #signalsStorageService = inject(SignalsStoreService);
  #signalStoreService = inject(SignalsStoreService);
  #globalMessageService = inject(GlobalMessageService);
  #platformId = inject(PLATFORM_ID);

  //#endregion

  //#region Properties

  messageSlider = viewChild<ElementRef>('messageSlider');

  slider!: KeenSliderInstance;

  // Hold the messages back until after the first client render.
  //
  // Originally this guarded against a hydration mismatch: the server emitted N
  // message nodes while the client still had [] at the synchronous hydration
  // pass, and the node-count divergence crashed with `Cannot set properties of
  // null (setting '__ngContext__')`. Client hydration is no longer enabled (see
  // app.config.ts), so that crash is not reachable today.
  //
  // The gate stays because it still does something useful, and would be needed
  // again the moment hydration is switched back on: it keeps the banner out of
  // the very first client render, so the slider is only built once the slides
  // exist rather than mid-bootstrap.
  #hydrated = signal(false);
  messages = computed(() => this.#hydrated() ? this.#signalsStorageService.globalMessages() : []);

  //#endregion

  //#region Constructors

  constructor() {
    // Fetch on BOTH server and client so the banner is part of the SSR HTML.
    // On the server the HttpClient request blocks serialization until it
    // resolves, so `hasGlobalMessages` is accurate in the delivered markup — no
    // CLS when a message exists, and no empty 40px bar when none is configured.
    //
    // The client repeats the request. It used to be served from Angular's
    // TransferCache, but that ships inside provideClientHydration, which is no
    // longer enabled — so this is a real second round-trip. Cheap (one small
    // GET) and it keeps the banner current; worth revisiting alongside any
    // decision to re-enable hydration.
    this.#getGlobalMessages();

    // Release the gate after the first render — see the note on #hydrated.
    afterNextRender(() => this.#hydrated.set(true));

    // KeenSlider is browser-only and needs the rendered DOM element. React to the
    // messages signal (populated above) + the viewChild so the slider initializes
    // exactly once the slides are in the DOM.
    effect(() => {
      if (!isPlatformBrowser(this.#platformId)) return;
      const msgs = this.messages();
      const el = this.messageSlider();
      if (msgs.length && el && !this.slider) {
        this.#setUpSlide(msgs.length > 1);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.slider) this.slider.destroy();
  }

  //#endregion

  //#region Methods

  #setUpSlide(loop: boolean) {

    try {

      this.slider = new KeenSlider(this.messageSlider()?.nativeElement, {
        initial: 0,
        renderMode: 'performance',
        loop,
        drag: false,
        vertical: true
      }, [
        (slider) => {
          let timeout: NodeJS.Timeout | null = null;
          let mouseOver = false
          function clearNextTimeout() {
            if (timeout)
              clearTimeout(timeout);
          }
          function nextTimeout() {

            if (timeout)
              clearTimeout(timeout);

            if (mouseOver) return
            timeout = setTimeout(() => {
              slider.next()
            }, 1000 * 5)
          }
          slider.on("created", () => {
            slider.container.addEventListener("mouseover", () => {
              mouseOver = true
              clearNextTimeout()
            })
            slider.container.addEventListener("mouseout", () => {
              mouseOver = false
              nextTimeout()
            })
            nextTimeout()
          })
          slider.on("dragStarted", clearNextTimeout)
          slider.on("animationEnded", nextTimeout)
          slider.on("updated", nextTimeout)
        }
      ]);

    } catch (error) {
      console.log('error', error);
    }
  }

  #getGlobalMessages() {
    // Browser-only: never fetch/populate during SSR. The server must render the
    // banner empty so it matches the client's (empty) first paint; the client
    // fetches and reveals it after hydration. Keeps `hasGlobalMessages` (which
    // drives the host's classList) consistent server↔client during hydration.
    if (!isPlatformBrowser(this.#platformId)) return;

    this.#globalMessageService
      .getGlobalMessage()
      .pipe(
        map(res => {
          return (res ?? []).filter(x => this.#signalStoreService.hasSession() ? x.visibleWithSesion : x.visibleWithoutSesion);
        }),
        tap(res => this.#signalStoreService.globalMessages.set(res)),
      )
      .subscribe();
  }

  //#endregion
}
